/**
 * @file chat module entry
 * @author malcolmmzma
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, PopOver, Select, SelectProps, OverlayTrigger } from '@tencent/spaui';
import AdqIcon from '@tencent/adq-icons';
import _ from 'lodash';
import { ChatInput, ChatInputProps, AskOption, ChatInputRef } from '@tencent/ai-chat-react';
import { useMemoizedFn } from 'ahooks';
import { Flex, genToken, PullDownChip, Text, Link, Frame, Divider } from '@tencent/odc';

import { uploadFile } from '../../../api/analysis';
import reportHub from '@/common/report-hub';
import globalStore from '@/stores';

import RecommendPromptList from '../components/recommend-prompt-list';
import { DEFAULT_PROMPT, DEFAULT_RECOMMEND_PROMPTS, DEFAULT_QUERY_PROMPTS, AGENT_LIST, AGENT_MAP } from '../constants/agent';
import ChatInstance, { ChatEngine } from '../chat/instance';
import AgentContainer from '../components/container';
import { SEND_USER_PROMPT, CLICK_BOARD_GENERATE_PROMPT, CLICK_DATA_QUERY_PROMPT } from '../constants/report';

import '../index.less';
import { useHistory } from 'react-router-dom';
import { useSearchParams } from '../hooks/useSearchParams';
import useTypeAnimation from '../hooks/useTypeAnimation';
import classNames from 'classnames';
import PromptOptimize from '../components/PromptOptimize';
import { createSessionId } from '../../../api';

export interface ChatProps extends React.HTMLProps<HTMLDivElement> {
  userInfo?: any; // 用户信息
}

const Chat = (props: ChatProps) => {
  const { } = props;

  const history = useHistory();

  const { account } = globalStore;

  // UI 动画
  const { displayedText: placeholder } = useTypeAnimation(DEFAULT_PROMPT, { speed: 50, delay: 700 }); // 使用自定义的打字机动画钩子
  const [expanded, setExpanded] = useState(false);
  // UI state
  const [chatting, setChatting] = useState(false);
  const [recommendPromptLoading, setRecommendPromptLoading] = useState(false);

  // states
  const [openAgentSelect, setOpenAgentSelect] = useState(false);
  const [submittedPrompt, setSubmittedPrompt] = useState(DEFAULT_PROMPT);
  const [files, setFiles] = useState<AskOption['file_list']>([]);
  const [activeAgent, setActiveAgent] = useState<keyof typeof AGENT_MAP>(AGENT_LIST[0].value);
  const [debugAgentId, setDebugAgentId] = useState(new URLSearchParams(window.location.search).get('agent_id'));
  const [chatSessionId, setChatSessionId] = useState<string>();

  const chatEngineRef = useRef<ChatEngine | null>(null);
  const inputRef = useRef<ChatInputRef>(null);
  /** 为 true 时表示在等后端 sessionId，此时忽略 SDK 的 onChatSessionIdChange(uuid)，避免用错 id */
  const backendSessionIdPendingRef = useRef(false);

  const { searchParamStr } = useSearchParams();

  /** 会话 id 就绪后的统一处理：上报 + 更新 URL */
  const handleSessionIdChange = useCallback(
    (sessionId: string, prompt: string) => {
      reportHub.reportAction({
        action: SEND_USER_PROMPT,
        prompt,
        session_id: sessionId,
      });
      history.replace(`/analysis/ai-agent/${sessionId}?${searchParamStr}`);
    },
    [searchParamStr],
  );

  /**
   * 不改 SDK 方案：先进入会话（无 loading），后台拉取后端 sessionId，再通过 props 传给 ChatInstance。
   * ChatInstance 会在拿到 sessionId 后再发首条消息，保证 chatStream 用后端 sessionId。
   */
  const handleSubmitToChat = useCallback<Required<ChatInputProps>['onSend']>(
    ({ prompt, file_list: fileList = [] }) => {
      const promptText = prompt || DEFAULT_PROMPT;
      setSubmittedPrompt(promptText);
      setFiles(fileList);
      setChatting(true);

      backendSessionIdPendingRef.current = true;
      const agentId = Number(debugAgentId) || activeAgent;
      createSessionId({ agent_id: agentId, title: promptText })
        .then((newSessionId) => {
          backendSessionIdPendingRef.current = false;
          setChatSessionId(newSessionId);
          handleSessionIdChange(newSessionId, promptText);
        })
        .catch(() => {
          backendSessionIdPendingRef.current = false;
        });

      return true;
    },
    [activeAgent, debugAgentId, handleSessionIdChange],
  );

  // 图片上传
  const uploadImage = useMemoizedFn<Required<Required<ChatInputProps>['uploadOptions']>['uploadAction']>(async (file) => {
    if (file.fileFrom !== 'local') {
      return;
    }

    const res = await uploadFile({ file: file.file });
    const { code, data, message: errMsg } = res || {};
    if (code !== 0) {
      throw new Error(errMsg);
    }

    return data;
  });

  const handleSelectAgent = useCallback<SelectProps['onChange']>((_e: any, modelId: keyof typeof AGENT_MAP) => {
    setActiveAgent(modelId);
    setDebugAgentId(null);
  }, []);

  const handleLoadMore = useCallback(() => {
    // 处理加载更多
    setRecommendPromptLoading(true);
    setTimeout(() => {
      setRecommendPromptLoading(false);
      // 模拟加载更多数据
      // 实际应用中可以在这里调用 API 获取更多推荐数据
    }, 2000);
  }, []);

  useEffect(() => {
    // 在组件挂载后触发动画;
    setTimeout(() => {
      setExpanded(true);
    }, 400);
  }, []);

  return (
    <AgentContainer>
      {chatting ? (
        <div className="tw-w-full tw-h-full tw-relative">
          <ChatInstance
            initialPrompt={submittedPrompt}
            ref={chatEngineRef}
            fileUrlList={_.map(files, ({ url }) => url || '')}
            agentId={Number(debugAgentId)}
            modelId={activeAgent}
            chatSessionId={chatSessionId}
            onChatSessionIdChange={(sessionId) => {
              if (backendSessionIdPendingRef.current) return;
              handleSessionIdChange(sessionId, submittedPrompt);
            }}
          />
        </div>
      ) : (
        <div className="ai-agent-home tw-h-full tw-px-4 tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-10">
          <div className="tw-relative tw-w-full tw-max-w-5xl tw-flex tw-flex-col tw-gap-[96px] tw-z-10">
            <div className="tw-flex tw-justify-center tw-items-center">
              <div className="tw-flex tw-flex-col tw-gap-3 tw-items-center">
                <p className="tw-font-semibold tw-text-4xl tw-gap-5 tw-flex tw-items-center">
                  <img src="https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/1765371462074.png?max_age=31536000" width="40px" />
                  Hi {_.get(account, 'operator_name', '亲爱的用户')}，今天有什么可以帮到你？
                </p>
                <Text label="我是你的专属AI助理，懂你所需，助你所为" size="large" type="comment" />
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-6">
              <ChatInput
                ref={inputRef}
                className={classNames({
                  'ai-chat-react-input__unexpanded': !expanded,
                })}
                placeholder={placeholder}
                onSend={handleSubmitToChat}
                uploadOptions={{ enabled: true, uploadAction: uploadImage }}
                autoFocus
                bottomComponents={{
                  left: [
                    <PromptOptimize
                      key="prompt-optimize"
                      getPrompt={() => inputRef.current?.getPrompt?.() || DEFAULT_PROMPT}
                      onOptimize={(optimizedPrompt) => {
                        inputRef.current?.setPrompt(optimizedPrompt);
                      }}
                    />,
                    <Select
                      key="agent-select"
                      // for对客，禁用自主模型选择，默认第一个；from balawang
                      className="tw-hidden"
                      data={AGENT_LIST}
                      search={false}
                      onChange={handleSelectAgent}
                      onOpen={(_e: any, v: boolean) => setOpenAgentSelect(v)}
                      value={activeAgent}
                      dropStyle={{
                        ...genToken({
                          selectDropW: '240px',
                          selectInfoHeight: '100%',
                        }),
                      }}
                      height={32}
                      SelectionItemDataComponent={({ data }: any) => (
                        <Flex className="w-full tw-py-[7px]" column>
                          <Text style={{ lineHeight: '22px' }}>{data.label}</Text>
                          {data.description && (
                            <Text type="comment" size="small">
                              {data.description}
                            </Text>
                          )}
                        </Flex>
                      )}
                      ValueAreaComponent={(data: any) => {
                        const { open: selectOpen } = data;
                        return (
                          <PullDownChip
                            className="tw-w-[80px] tw-bg-white-00 hover:tw-bg-black-01 tw-text-sm tw-text-text-secondary focus:tw-bg-black-01 tw-rounded-full"
                            active={selectOpen}
                            text={
                              debugAgentId
                                ? `ID:${debugAgentId}`
                                : _.get(AGENT_MAP, [activeAgent, 'name'], '请选择模型')
                            }
                            mode="filled"
                            onClick={(_e: any, v: boolean) => setOpenAgentSelect(v)}
                            style={{
                              width: '100%',
                              height: 32,
                            }}
                          />
                        );
                      }}
                      open={openAgentSelect}
                      mountTarget="body"
                    />,
                    <>
                      <Divider type="vertical" gap={0} />
                      <Flex gap={4} className='tw-w-full tw-ml-2 tw-flex-1' wrap>
                        {_.map(DEFAULT_QUERY_PROMPTS, (queryPrompt) => (
                          <div
                            key={queryPrompt}
                            className='tw-border-[#495A7A29] tw-border-[1px] tw-rounded-[6px] tw-cursor-pointer tw-h-[18px] tw-py-0 tw-px-[8px]'
                            onClick={() => {
                              inputRef.current?.setPrompt(queryPrompt);
                              reportHub.reportAction({
                                action: CLICK_DATA_QUERY_PROMPT,
                                prompt: queryPrompt,
                              });
                            }}
                          >
                            <Text block label={queryPrompt} size="small" type="secondary" style={{ lineHeight: '16px', whiteSpace: 'nowrap' }} />
                          </div>
                        ))}
                      </Flex>
                    </>,
                  ],
                }}
              />
              <div className="recommend-prompt tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-justify-between tw-items-center">
                  <Text label="为你推荐以下数据场景"></Text>
                  <Button
                    className="tw-text-text-caption !tw-hidden"
                    displayType="text"
                    icon={<AdqIcon name="refresh" />}
                    onClick={handleLoadMore}
                  >
                    推荐更多
                  </Button>
                  <OverlayTrigger
                    overlay={
                      <PopOver style={genToken({ popoverPadding: '16px', popoverMaxWidth: '360px', fontSize: '12px' })}>
                        <Text className='tw-mb-[8px]' block label="目前支持分析范围：" type='primary' as='strong' size='small' />
                        <Frame grid cols={2} style={{ gridTemplateColumns: '58px auto', columnGap: 16, rowGap: 4 }} alignItems="start">
                          <Text label="分析维度" type="comment" as='strong' size='small' />
                          <Text label="账户、主体、业务单元、服务商、营销单元、创意、推广产品（ID/类型）、版位、浅层优化目标、深层优化目标、受众特征（性别/年龄）、日期" size='small' />
                          <Text label="分析指标" type="comment" as='strong' size='small' />
                          <Text label="点击量/率、曝光量、实际花费、浅层转化量/率、深层转化量/率、视频 3s 播放完成率、CPC、CPM、目标转化成本、平均有效播放时长、视频有效播放总时长、3s 播放完成次数、视频有效播放次数" size='small' />
                          <Text label="时间范围" type="comment" as='strong' size='small' />
                          <Text label="近 2 周" size='small' />
                        </Frame>
                      </PopOver>
                    }
                  >
                    <div>
                      <Text
                        className='tw-cursor-pointer'
                        type="link"
                        label="查看数据范围"
                      />
                    </div>
                  </OverlayTrigger>
                </div>
                <RecommendPromptList
                  loading={recommendPromptLoading}
                  data={DEFAULT_RECOMMEND_PROMPTS}
                  onClick={(item) => {
                    inputRef.current?.setPrompt(`${item.title}：${item.description}`);
                    reportHub.reportAction({
                      action: CLICK_BOARD_GENERATE_PROMPT,
                      title: item.title,
                      description: item.description,
                      prompt: `${item.title}：${item.description}`,
                    });
                  }}
                />
              </div>
            </div>
          </div>
          <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
            <Link
              href="https://docs.qq.com/doc/DSU1HbWxqbWxydVFG?nlc=1"
              target="_blank"
            >发现更多实用指南</Link>
          </div>
        </div>
      )}
    </AgentContainer>
  );
};

export default Chat;
